import React, { createContext, useState, useEffect, useContext } from 'react';
import hiveService from '../services/hive';
import { userApi } from '../services/api';
import keychainAuth from '../services/keychainAuth';
import { encryptPostingKey, decryptPostingKey } from '../utils/crypto';
import InvalidPostingKeyModal from '../components/InvalidPostingKeyModal/InvalidPostingKeyModal';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const STORAGE_KEYS = {
  USERNAME: 'hive_username',
  ENCRYPTED_KEY: 'hive_encrypted_key',
  LOGIN_METHOD: 'hive_login_method',
  LAST_ACTIVITY: 'hive_last_activity',
  SESSION_START: 'hive_session_start',
  SAVED_ACCOUNTS: 'hive_saved_accounts',
  DECRYPTED_POSTING_KEY: 'hive_decrypted_posting_key',
  WEB2_TOKEN: 'hive_web2_token'
};

const MAX_INACTIVITY_MS = 3 * 60 * 60 * 1000; // first number is hours
const MAX_SESSION_MS = 24 * 60 * 60 * 1000; // 24 hours

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isKeychainInstalled, setIsKeychainInstalled] = useState(false);
  const [loginMethod, setLoginMethod] = useState(null); // 'keychain' or 'postingkey'
  const [showKeyInvalidModal, setShowKeyInvalidModal] = useState(false);
  const [keyInvalidUsername, setKeyInvalidUsername] = useState(null);
  const [syncPromptData, setSyncPromptData] = useState(null);

  // Check session validity
  const checkSessionValidity = () => {
    const lastActivity = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
    const sessionStart = localStorage.getItem(STORAGE_KEYS.SESSION_START);
    const now = Date.now();

    if (!lastActivity || !sessionStart) {
      return false;
    }

    const timeSinceActivity = now - parseInt(lastActivity);
    const timeSinceStart = now - parseInt(sessionStart);

    // Check if session expired
    if (timeSinceActivity > MAX_INACTIVITY_MS || timeSinceStart > MAX_SESSION_MS) {
      return false;
    }

    return true;
  };

  // Update last activity timestamp
  const updateActivity = () => {
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
  };

  // Restore session on mount
  useEffect(() => {
    const restoreSession = () => {
      const username = localStorage.getItem(STORAGE_KEYS.USERNAME);
      const method = localStorage.getItem(STORAGE_KEYS.LOGIN_METHOD);
      
      if (username && method && checkSessionValidity()) {
        setUser({ username });
        setLoginMethod(method);
        
        // For posting key logins, restore decrypted key from sessionStorage
        if (method === 'postingkey') {
          const decryptedKey = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_POSTING_KEY);
          if (decryptedKey) {
            hiveService.setLoginMethod('postingkey', decryptedKey);
            updateActivity();

            // Background verification — non-blocking. If the key is no longer valid
            // on-chain (e.g. user rotated keys elsewhere), fire the invalid event so
            // the modal is shown without blocking the page load.
            hiveService.verifyPostingKey(username, decryptedKey).catch(() => {
              // verifyPostingKey rejects if the key is wrong
              hiveService.notifyPostingKeyInvalid(username, 'Stored posting key no longer valid');
            });
          } else {
            // Session valid but key not in sessionStorage (browser restart)
            // Clear session - user must re-login with PIN
            clearSession();
            return;
          }
        } else {
          // Keychain login
          hiveService.setLoginMethod(method);
          updateActivity();
        }
      } else if (username) {
        // Session expired, clean up
        clearSession();
      }
    };

    restoreSession();

    // Check for Keychain
    const checkKeychain = () => {
      const installed = hiveService.isKeychainInstalled();
      setIsKeychainInstalled(installed);
      setLoading(false);
    };

    checkKeychain();
    
    // Retry Keychain detection
    const timer1 = setTimeout(checkKeychain, 500);
    const timer2 = setTimeout(checkKeychain, 1000);
    const timer3 = setTimeout(checkKeychain, 2000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  // Listen for hive:posting-key-invalid events dispatched from hive.js
  // (fired when a broadcast fails with an auth error, or on session restore verification)
  useEffect(() => {
    const handleKeyInvalid = (e) => {
      const affectedUsername = e.detail?.username || localStorage.getItem(STORAGE_KEYS.USERNAME);
      if (affectedUsername) {
        setKeyInvalidUsername(affectedUsername);
        setShowKeyInvalidModal(true);
      }
    };

    window.addEventListener('hive:posting-key-invalid', handleKeyInvalid);
    return () => window.removeEventListener('hive:posting-key-invalid', handleKeyInvalid);
  }, []);

  // Track user activity
  useEffect(() => {
    if (user) {
      const handleActivity = () => {
        if (checkSessionValidity()) {
          updateActivity();
        } else {
          // Session expired
          logout();
          alert('Your session has expired. Please login again.');
        }
      };

      window.addEventListener('mousedown', handleActivity);
      window.addEventListener('keydown', handleActivity);
      window.addEventListener('touchstart', handleActivity);

      return () => {
        window.removeEventListener('mousedown', handleActivity);
        window.removeEventListener('keydown', handleActivity);
        window.removeEventListener('touchstart', handleActivity);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // When user is set, fetch their profile (includes normalized profileImage/coverImage)
  useEffect(() => {
    const fetchProfileForUser = async () => {
      if (user && user.username && !user.profile) {
        try {
          const data = await userApi.getProfile(user.username);
          if (data && data.user) {
            setUser(prev => ({ ...prev, profile: data.user.profile }));
          }
        } catch (err) {
          console.error('Error fetching user profile in AuthContext:', err);
        }
        // Silently restore proflex if another frontend wiped it (posting-key only, fire-and-forget)
        hiveService.checkAndRestoreProflex(user.username)
          .then(result => { if (result?.restored) console.info('[AuthContext] proflex restored for @' + user.username); })
          .catch(() => {});
      }

    };
    fetchProfileForUser();
  }, [user]);

  // On mount, ensure saved accounts have cached profile images
  useEffect(() => {
    const fetchSavedProfiles = async () => {
      try {
        const saved = getSavedAccounts();
        const usernames = Object.keys(saved || {});
        for (const u of usernames) {
          if (!saved[u].profileImage) {
            try {
              const data = await userApi.getProfile(u);
              const img = data?.user?.profile?.profileImage || null;
              if (img) {
                saved[u].profileImage = img;
              }
            } catch (err) {
              // Continue on individual failures
            }
          }
        }
        localStorage.setItem(STORAGE_KEYS.SAVED_ACCOUNTS, JSON.stringify(saved));
      } catch (err) {
        // Ignore overall failures
      }
    };
    fetchSavedProfiles();
  }, []);

  const clearSession = () => {
    localStorage.removeItem(STORAGE_KEYS.USERNAME);
    localStorage.removeItem(STORAGE_KEYS.ENCRYPTED_KEY);
    localStorage.removeItem(STORAGE_KEYS.LOGIN_METHOD);
    localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
    localStorage.removeItem(STORAGE_KEYS.SESSION_START);
    localStorage.removeItem(STORAGE_KEYS.WEB2_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.DECRYPTED_POSTING_KEY);
    hiveService.setLoginMethod(null, null);
  };

  // Login with email/password (Web2 method)
  const loginWithEmail = async (emailOrUsername, password) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailOrUsername, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      const { username, token, postingKey } = data;

      // Store session info
      const now = Date.now().toString();
      localStorage.setItem(STORAGE_KEYS.USERNAME, username);
      localStorage.setItem(STORAGE_KEYS.LOGIN_METHOD, 'web2');
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now);
      localStorage.setItem(STORAGE_KEYS.SESSION_START, now);
      localStorage.setItem(STORAGE_KEYS.WEB2_TOKEN, token);

      // If posting key provided, store it for operations
      if (postingKey) {
        sessionStorage.setItem(STORAGE_KEYS.DECRYPTED_POSTING_KEY, postingKey);
        hiveService.setLoginMethod('postingkey', postingKey);
      }

      setUser({ username });
      setLoginMethod('web2');

      // Best-effort register with notification system
      registerNotifications(username);

      // Save to saved accounts
      saveAccountToStorage(username, null, 'web2');

      return username;
    } catch (error) {
      console.error('Email login error:', error);
      throw error;
    }
  };

  // Universal login method that handles all auth types
  const login = async (credentials) => {
    const { username, postingKey, method } = credentials;

    try {
      if (method === 'keychain') {
        // Keychain login
        return await loginWithKeychain(username);
      } else if (method === 'password' && postingKey) {
        // Password-based login (posting key provided)
        return await loginWithPostingKey(username, postingKey, null);
      } else {
        throw new Error('Invalid login credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Login with posting key
  const loginWithPostingKey = async (username, postingKey, pin = null) => {
    try {
      // Verify the posting key is valid
      await hiveService.verifyPostingKey(username, postingKey);

      // If PIN provided, encrypt it (legacy mode)
      let encryptedKey = null;
      if (pin) {
        encryptedKey = encryptPostingKey(postingKey, pin);
        localStorage.setItem(STORAGE_KEYS.ENCRYPTED_KEY, encryptedKey);
      }

      // Store session info
      const now = Date.now().toString();
      localStorage.setItem(STORAGE_KEYS.USERNAME, username);
      localStorage.setItem(STORAGE_KEYS.LOGIN_METHOD, 'postingkey');
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now);
      localStorage.setItem(STORAGE_KEYS.SESSION_START, now);

      // Store decrypted key in sessionStorage for page refreshes
      sessionStorage.setItem(STORAGE_KEYS.DECRYPTED_POSTING_KEY, postingKey);

      // Set posting key in hive service (in memory only)
      hiveService.setLoginMethod('postingkey', postingKey);

      setUser({ username });
      setLoginMethod('postingkey');

      // Best-effort register with notification system
      registerNotifications(username);
      // Refresh subscriptions in background (non-blocking)
      triggerSubscriptionsRefresh(username);

      // Save to saved accounts if PIN was used
      if (pin && encryptedKey) {
        saveAccountToStorage(username, encryptedKey);
      }

      return username;
    } catch (error) {
      console.error('Posting key login error:', error);
      throw error;
    }
  };

  // Login with Keychain
  const loginWithKeychain = async (username = null) => {
    try {
      // Use Keychain's account selection if no username provided
      if (!username) {
        // Keychain will show its own account picker
        const result = await keychainAuth.loginWithAccountPicker();
        username = result.username;
      } else {
        // Specific username provided - verify with Keychain
        const result = await keychainAuth.login(username);
        username = result.username;
      }

      // Store session info
      const now = Date.now().toString();
      localStorage.setItem(STORAGE_KEYS.USERNAME, username);
      localStorage.setItem(STORAGE_KEYS.LOGIN_METHOD, 'keychain');
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now);
      localStorage.setItem(STORAGE_KEYS.SESSION_START, now);

      // Ensure stale posting-key session data cannot override active Keychain auth.
      sessionStorage.removeItem('hive_posting_key');

      // Set login method in hive service
      hiveService.setLoginMethod('keychain');

      setUser({ username });
      setLoginMethod('keychain');

      // Best-effort register with notification system
      registerNotifications(username);        // Refresh subscriptions in background (non-blocking)
        triggerSubscriptionsRefresh(username);
      // Save to saved accounts
      saveAccountToStorage(username, null, 'keychain');

      return username;
    } catch (error) {
      console.error('Keychain login error:', error);
      throw error;
    }
  };

  // Unlock account with PIN (for posting key login)
  const unlockWithPin = async (pin) => {
    try {
      const encryptedKey = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_KEY);
      const username = localStorage.getItem(STORAGE_KEYS.USERNAME);

      if (!encryptedKey || !username) {
        throw new Error('No encrypted key found');
      }

      // Decrypt posting key
      const postingKey = decryptPostingKey(encryptedKey, pin);

      // Verify it's still valid
      await hiveService.verifyPostingKey(username, postingKey);

      // Set in hive service
      hiveService.setLoginMethod('postingkey', postingKey);

      updateActivity();

      return true;
    } catch (error) {
      throw new Error('Invalid PIN or posting key verification failed');
    }
  };

  // Logout
  const logout = () => {
    clearSession();
    setUser(null);
    setLoginMethod(null);
  };

  // Get all saved accounts
  const getSavedAccounts = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.SAVED_ACCOUNTS);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading saved accounts:', error);
      return {};
    }
  };

  // Save account to storage (optionally include profileImage). If profileImage is not provided we fetch it in background.

  // Register user with notification backend (best-effort, non-blocking)
  const registerNotifications = async (username) => {
    try {
      const resp = await fetch('/api/notifications/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username })
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.isNewUser) {
          // New user: silently write default beneficiaries to proflex (posting-key only)
          hiveService.updateProflexDefaultBeneficiaries(username, [{ account: 'flexus', weight: 3 }])
            .catch(err => console.warn('[AuthContext] proflex beneficiary seed failed:', err.message));
        }
      }
    } catch (err) {
      // Non-fatal: log and continue
      console.error('registerNotifications error', err);
    }
  };

  // Trigger a non-blocking subscriptions refresh on the server (with a local staleness check)
  const SUBSCRIPTIONS_STALE_MS = 24 * 60 * 60 * 1000; // 24 hours
  const triggerSubscriptionsRefresh = async (username, { force = false } = {}) => {
    try {
      if (!username) return;
      const key = `subscriptions:lastSync:${username}`;
      const last = localStorage.getItem(key);
      if (!force && last && (Date.now() - parseInt(last, 10)) < SUBSCRIPTIONS_STALE_MS) {
        // Recent local sync - skip network call
        return;
      }

      // Fire-and-forget POST to backend; backend also enforces staleness and pending locks
      fetch(`/api/user/${encodeURIComponent(username)}/subscriptions/refresh${force ? '?force=true' : ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      })
      .then(async (resp) => {
        if (resp.ok) {
          const data = await resp.json();
          if (data.success) {
            localStorage.setItem(key, Date.now().toString());
          } else if (data.lastSync) {
            localStorage.setItem(key, data.lastSync.toString());
          }
        }
      })
      .catch((err) => {
        console.warn('Subscriptions refresh request failed', err);
      });
    } catch (err) {
      console.warn('triggerSubscriptionsRefresh error', err);
    }
  };

  const saveAccountToStorage = async (username, encryptedKey, type = 'postingkey', profileImage = null) => {
    try {
      const savedAccounts = getSavedAccounts();
      if (type === 'keychain') {
        savedAccounts[username] = { type: 'keychain', profileImage: profileImage || null };
      } else {
        savedAccounts[username] = { type: 'postingkey', encryptedKey, profileImage: profileImage || null };
      }
      localStorage.setItem(STORAGE_KEYS.SAVED_ACCOUNTS, JSON.stringify(savedAccounts));

      // If no profileImage provided, fetch and cache it asynchronously
      if (!profileImage) {
        try {
          const data = await userApi.getProfile(username);
          const img = data?.user?.profile?.profileImage || null;
          if (img) {
            const updated = getSavedAccounts();
            if (updated[username]) {
              updated[username].profileImage = img;
              localStorage.setItem(STORAGE_KEYS.SAVED_ACCOUNTS, JSON.stringify(updated));
            }
          }
        } catch (err) {
          // Ignore failures silently
        }
      }
    } catch (error) {
      console.error('Error saving account:', error);
    }
  };

  // Remove account from storage
  const removeAccount = (username) => {
    try {
      const savedAccounts = getSavedAccounts();
      delete savedAccounts[username];
      localStorage.setItem(STORAGE_KEYS.SAVED_ACCOUNTS, JSON.stringify(savedAccounts));
    } catch (error) {
      console.error('Error removing account:', error);
    }
  };

  // Login with saved account using PIN
  const loginWithSavedAccount = async (username, pin = null) => {
    const savedAccounts = getSavedAccounts();
    const account = savedAccounts[username];

    if (!account) {
      throw new Error('Account not found');
    }

    // Handle Keychain accounts
    if (account.type === 'keychain') {
      return await loginWithKeychain(username);
    }

    // Handle posting key accounts
    if (!pin) {
      throw new Error('PIN required for posting key accounts');
    }

    // Decrypt posting key
    let postingKey;
    try {
      postingKey = decryptPostingKey(account.encryptedKey, pin);
    } catch (e) {
      const err = new Error('Incorrect PIN');
      err.code = 'INVALID_PIN';
      throw err;
    }

    // Verify it's still valid on the blockchain
    try {
      await hiveService.verifyPostingKey(username, postingKey);
    } catch (verifyErr) {
      const err = new Error('Posting key verification failed (key may have changed)');
      err.code = 'KEY_OUTDATED';
      throw err;
    }

    // Store session info
    const now = Date.now().toString();
    localStorage.setItem(STORAGE_KEYS.USERNAME, username);
    localStorage.setItem(STORAGE_KEYS.ENCRYPTED_KEY, account.encryptedKey);
    localStorage.setItem(STORAGE_KEYS.LOGIN_METHOD, 'postingkey');
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now);
    localStorage.setItem(STORAGE_KEYS.SESSION_START, now);

    // Store decrypted key in sessionStorage for page refreshes
    sessionStorage.setItem(STORAGE_KEYS.DECRYPTED_POSTING_KEY, postingKey);

    // Set posting key in hive service
    hiveService.setLoginMethod('postingkey', postingKey);

    setUser({ username });
    setLoginMethod('postingkey');

    // Best-effort register with notification system
    registerNotifications(username);
    // Refresh subscriptions in background (non-blocking)
    triggerSubscriptionsRefresh(username);

    return username;
  };

  /**
   * Check if user has a stored posting key in the backend environment.
   * If yes, trigger prompt to update stored key for autovoting and scheduled posts.
   */
  const checkPromptUpdateStoredKey = async (username, newPostingKey) => {
    try {
      const status = await userApi.getStoredPostingKeyStatus(username);
      if (status && status.hasKey) {
        setSyncPromptData({ username, newPostingKey });
      }
    } catch (err) {
      console.warn('Failed to check stored posting key status for prompt:', err.message);
    }
  };

  const isAuthenticated = user !== null;

  const handleKeyInvalidDismiss = () => {
    setShowKeyInvalidModal(false);
    setKeyInvalidUsername(null);
  };

  const handleKeyInvalidUpdated = (newKey) => {
    // The modal already updated sessionStorage + hiveService. Just close it.
    setShowKeyInvalidModal(false);
    setKeyInvalidUsername(null);
  };

  const handleConfirmSyncStoredKey = async () => {
    if (!syncPromptData) return;
    const { username, newPostingKey } = syncPromptData;
    try {
      await userApi.storePostingKey(username, newPostingKey);
      alert('Stored posting key for autovoting and scheduled posts updated successfully.');
    } catch (err) {
      alert('Failed to update stored posting key: ' + (err.response?.data?.error || err.message));
    } finally {
      setSyncPromptData(null);
    }
  };

  const handleDismissSyncStoredKey = () => {
    setSyncPromptData(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login,
      loginWithPostingKey,
      loginWithKeychain,
      loginWithEmail,
      loginWithSavedAccount,
      unlockWithPin,
      logout, 
      loading,
      isAuthenticated,
      isKeychainInstalled,
      loginMethod,
      getSavedAccounts,
      removeAccount,
      checkPromptUpdateStoredKey
    }}>
      {children}
      {showKeyInvalidModal && keyInvalidUsername && (
        <InvalidPostingKeyModal
          username={keyInvalidUsername}
          onUpdated={handleKeyInvalidUpdated}
          onDismiss={handleKeyInvalidDismiss}
        />
      )}
      {syncPromptData && (
        <div className="ipkm-overlay" role="dialog" aria-modal="true" style={{ zIndex: 10000 }}>
          <div className="ipkm-dialog">
            <div className="ipkm-header">
              <span className="ipkm-icon">🔑</span>
              <h2 className="ipkm-title">Update Stored Posting Key?</h2>
              <button
                className="ipkm-close"
                aria-label="Dismiss"
                onClick={handleDismissSyncStoredKey}
                type="button"
              >
                ×
              </button>
            </div>
            <div className="ipkm-body">
              <p className="ipkm-description">
                You updated the posting key for <strong>@{syncPromptData.username}</strong>.
              </p>
              <p className="ipkm-tip">
                You have a stored posting key configured for autovoting and scheduled posts.
                Would you like to update your stored key to keep those services working seamlessly?
              </p>
              <div className="ipkm-actions">
                <button
                  type="button"
                  className="ipkm-btn ipkm-btn-primary"
                  onClick={handleConfirmSyncStoredKey}
                >
                  Yes, Update Stored Key
                </button>
                <button
                  type="button"
                  className="ipkm-btn ipkm-btn-ghost"
                  onClick={handleDismissSyncStoredKey}
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
